import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Fingerprint, KeyRound } from "lucide-react";
import logo from "@/assets/logo-gerio.png";

export function BiometricGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    if (!user || !isMobile) {
      setVerified(true);
      setChecking(false);
      return;
    }

    // Check if already verified this session
    if (sessionStorage.getItem("gerio-bio-verified") === "true") {
      setVerified(true);
      setChecking(false);
      return;
    }

    const checkBio = async () => {
      try {
        const { data } = await supabase
          .from("profiles")
          .select("biometrics_enabled")
          .eq("user_id", user.id)
          .single();
        if (data?.biometrics_enabled) {
          promptBiometric();
        } else {
          setVerified(true);
          setChecking(false);
        }
      } catch {
        setVerified(true);
        setChecking(false);
      }
    };
    checkBio();
  }, [user, isMobile]);

  const promptBiometric = async () => {
    setChecking(true);
    try {
      if (!window.PublicKeyCredential) {
        setVerified(true);
        setChecking(false);
        return;
      }

      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: crypto.getRandomValues(new Uint8Array(32)),
          timeout: 60000,
          userVerification: "required",
          rpId: window.location.hostname,
        },
      });

      if (credential) {
        sessionStorage.setItem("gerio-bio-verified", "true");
        setVerified(true);
      } else {
        setShowFallback(true);
      }
    } catch (err: any) {
      // User cancelled or error - show fallback
      setShowFallback(true);
    } finally {
      setChecking(false);
    }
  };

  const skipBiometric = () => {
    sessionStorage.setItem("gerio-bio-verified", "true");
    setVerified(true);
    setShowFallback(false);
  };

  if (verified) return <>{children}</>;

  if (checking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-6 p-6">
        <img src={logo} alt="Gerio" className="h-10" />
        <Fingerprint className="h-16 w-16 text-primary animate-pulse" />
        <p className="text-sm text-muted-foreground">A verificar identidade...</p>
      </div>
    );
  }

  if (showFallback) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-6 p-6">
        <img src={logo} alt="Gerio" className="h-10" />
        <Fingerprint className="h-16 w-16 text-muted-foreground" />
        <p className="text-sm text-center text-muted-foreground">
          Verificação biométrica cancelada ou falhou.
        </p>
        <div className="flex flex-col gap-3 w-full max-w-xs">
          <Button onClick={promptBiometric} className="w-full gap-2">
            <Fingerprint className="h-4 w-4" /> Tentar novamente
          </Button>
          <Button variant="outline" onClick={skipBiometric} className="w-full gap-2">
            <KeyRound className="h-4 w-4" /> Continuar sem biometria
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
